import React from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import axios from 'axios';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { calculatePercentChange, formatMetric } from 'src/utils/app';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  ToneType,
  VoiceAndToneMatchType,
  VoiceType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { StacklineTheme } from '@stackline/ui';
import { getWeekLastDate } from 'src/utils/dateformatting';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { Column } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProTable/BeaconProTable';
import { VoiceAndToneMatchAnalysisRow } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import colors from 'src/utils/colors';
type VoiceOrToneType = VoiceType | ToneType;

const DominantVoiceAndToneCell = ({ expected, actual }: { expected: VoiceOrToneType; actual: VoiceOrToneType }) => {
  const isAccurate = expected === actual;
  const icon = isAccurate ? (
    <div style={{ width: '8px', height: '8px', backgroundColor: colors.brandClubGreen, borderRadius: '100%' }} />
  ) : (
    <div style={{ width: '8px', height: '8px', backgroundColor: colors.errorRed, borderRadius: '100%' }} />
  );
  return (
    <Flex gap="sm" alignItems="center">
      {icon}
      <Text variant="body2">{actual}</Text>
    </Flex>
  );
};

//@ts-ignore
export function createProductColumn(width: string): Column<{ stacklineSku: string; product: Product }> {
  return {
    id: 'product',
    header: 'Products', //@ts-ignore
    width,
    renderCell: (row) => <ProductCell stacklineSku={row.stacklineSku} title={row.product.title} />
  };
}

export function creteProductColumWithSetWidth(width: string) {
  return {
    id: 'product',
    header: 'Products',
    renderCell: (row) => (
      <div style={{ width }}>
        <ProductCell stacklineSku={row.stacklineSku} title={row.product.title} />
      </div>
    )
  };
}

export function createWeekEndingColumn() {
  return {
    id: 'weekEnding',
    header: `Week${String.fromCharCode(160)}Ending`,
    width: '120px',
    renderCell: (row) => (
      <Text variant="body2" sx={{ padding: '0px' }}>
        {getWeekLastDate(row.weekId).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })}
      </Text>
    )
  };
}

const defaultOpts = { metricType: METRICTYPE.PERCENT, showPercentChange: true };
export function createMetricColumnDefinition(
  [id, header]: [string, string],
  opts?: { metricType?: METRICTYPE; showPercentChange?: boolean; cellStyle?: React.CSSProperties }
): Column<{ [key: string]: { value: number; prevValue: number } }> {
  const { metricType, showPercentChange, cellStyle } = { ...defaultOpts, ...opts };
  return {
    id,
    header,
    renderCell: (row) => {
      const value = row[id]?.value ?? 0;
      const comparisonValue = row[id]?.prevValue ?? 0;
      const percentChange = showPercentChange ? calculatePercentChange(value, comparisonValue) : undefined;

      return (
        <MetricCell metricType={metricType} value={value} percentChange={percentChange} wrapperStyle={cellStyle} />
      );
    }
  };
}

export const voiceAndToneHeaderTextStyle = {
  sx: { letterSpacing: '1.2px', fontWeight: '700', lineHeight: '20px' },
  variant: 'body3' as const
};

export const createVoiceAndToneMatchAnalysisColumnDefinitions = (
  theme: StacklineTheme
): Column<VoiceAndToneMatchAnalysisRow>[] => {
  const dominantVoiceColumn = {
    id: 'dominantVoice',
    header: (
      <>
        <Flex flexDirection="column">
          <Text {...voiceAndToneHeaderTextStyle}>Dominant</Text>
          <Text {...voiceAndToneHeaderTextStyle}>Voice</Text>
        </Flex>
      </>
    ),

    cellStyle: { width: '20px' },
    renderCell: (row: VoiceAndToneMatchAnalysisRow) => (
      <DominantVoiceAndToneCell expected={row.voice.expected} actual={row.voice.actual} />
    )
  };

  const dominantToneColumn = {
    id: 'dominantTone',
    header: (
      <>
        <Flex flexDirection="column">
          <Text {...voiceAndToneHeaderTextStyle}>Dominant</Text>
          <Text {...voiceAndToneHeaderTextStyle}>Tone</Text>
        </Flex>
      </>
    ),
    width: '96px',
    renderCell: (row: VoiceAndToneMatchAnalysisRow) => (
      <DominantVoiceAndToneCell expected={row.tone.expected} actual={row.tone.actual} />
    )
  };

  const nbsp = String.fromCharCode(160);
  const voiceMatchScoreColumn = {
    id: 'voiceMatchScore',
    header: `Voice${nbsp}Match Score`,
    width: '120px',
    renderCell: (row: VoiceAndToneMatchAnalysisRow) => (
      <Text variant="body2">{formatMetric(row.voice.score, METRICTYPE.PERCENT, { decimalPlaces: 1 })}</Text>
    )
  };

  const toneMatchScoreColumn = {
    id: 'toneMatchScore',
    header: `Tone${nbsp}Match Score`,
    width: '120px',
    renderCell: (row: VoiceAndToneMatchAnalysisRow) => (
      <Text variant="body2">{formatMetric(row.tone.score, METRICTYPE.PERCENT, { decimalPlaces: 1 })}</Text>
    )
  };

  const matchTypeColumn = {
    id: 'matchType',
    header: `Match${nbsp}Type`,
    width: '120px',
    renderCell: (row: VoiceAndToneMatchAnalysisRow) => {
      const matchType = (() => {
        switch (row.matchType) {
          case VoiceAndToneMatchType.FullMatch:
            return `Full${nbsp}Match`;
          case VoiceAndToneMatchType.ToneMismatch:
            return `Tone${nbsp}Mismatch`;
          case VoiceAndToneMatchType.VoiceMismatch:
            return `Voice${nbsp}Mismatch`;
          case VoiceAndToneMatchType.FullMismatch:
            return `Full${nbsp}Mismatch`;
          default:
            return '';
        }
      })();

      return (
        <Flex>
          <Text variant="body2">{matchType}</Text>
        </Flex>
      );
    }
  };
  return [dominantVoiceColumn, dominantToneColumn, voiceMatchScoreColumn, toneMatchScoreColumn, matchTypeColumn];
};

export enum ContentSubTabs {
  contentSummary = 'ContentSummary',
  contentUtilization = 'ContentUtilization',
  contentAccuracyV2 = 'ContentAccuracy',
  contentKeywords = 'KeywordPerformance',
  contentAccuracy = 'VoiceAndToneAnalysis',
  contentVariationsParent = 'VariationsParentView',
  contentVariationsChild = 'VariationsChildView'
}

interface DorisExportContentReq {
  startWeekId?: number;
  endWeekId?: number;
  comparisonStartWeekId?: number;
  comparisonEndWeekId?: number;
  retailerId: string;
  stacklineSkus?: string[];
  segmentIds?: string[];
  subCategoryIds?: number[];
  categoryIds?: number[];
  brandIds?: number[];
  exportType: ContentSubTabs;
  fileName?: string;
  columnDelimiter?: string;
  requestSource?: string;
  shouldGroupByWeek?: boolean;
}

export const exportContentReq = async ({
  startWeekId,
  endWeekId,
  comparisonStartWeekId,
  comparisonEndWeekId,
  retailerId = '1',
  segmentIds = [],
  subCategoryIds,
  categoryIds,
  stacklineSkus,
  brandIds,
  exportType,
  fileName,
  columnDelimiter = ',',
  requestSource = 'client',
  shouldGroupByWeek = false
}: DorisExportContentReq) => {
  const prefix = 'GroupByProduct';
  const url = '/api/beaconcontent/SubmitExportRequest';

  const body = {
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    stacklineSkus,
    retailerId,
    segmentIds,
    subCategoryIds,
    categoryIds,
    brandIds,
    exportType: `${prefix}${exportType}`,
    fileName,
    columnDelimiter,
    requestSource,
    shouldGroupByWeek
  };

  return axios.post(url, body);
};

export const dynamicRetailerScores = {
  default: ['Title Score', 'Bullets Score', 'Description Score', 'Image Score', 'Video Score', 'Enhanced Score'],
  rules: [
    {
      retailers: new Set([]),
      exclude: ['Bullets Score']
    },
    {
      retailers: new Set([]),
      exclude: ['Description Score']
    },
    {
      retailers: new Set([]),
      exclude: ['Image Score']
    },
    {
      retailers: new Set([63, 48, 73, 91, 75]),
      exclude: ['Video Score']
    },
    {
      retailers: new Set([35, 6, 63, 48, 73, 91, 75, 81, 78]),
      exclude: ['Enhanced Score']
    }
  ]
};

export const getRetailerScores = (retailerId) => {
  const { default: defaultScores, rules } = dynamicRetailerScores;
  const exclusions = new Set();

  rules.forEach((rule) => {
    if (rule.retailers.has(retailerId)) {
      rule.exclude.forEach((score) => exclusions.add(score));
    }
  });

  return defaultScores.filter((score) => !exclusions.has(score));
};

export const DEFAULT_KEYWORD_USAGE_THRESHOLD = 0.7;
export const DEFAULT_KEYWORD_PRODUCTIVITY_THRESHOLD = 0.7;
export const DEFAULT_KEYWORD_POPULARITY_THRESHOLD = 0.7;
